import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, Checkbox, FormControlLabel, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = '/leagues/';

const timelineOptions = [
  { value: '', label: '------' },
  { value: 'PAST', label: 'Past' },
  { value: 'ONGOING', label: 'Ongoing' },
  { value: 'UPCOMING', label: 'Upcoming' },
  { value: 'HOLD', label: 'Hold' },
];

const formatOptions = [
  { value: '', label: '------' },
  { value: 'LEAGUE', label: 'League' },
  { value: 'CUP', label: 'Cup' },
  { value: 'TOURNAMENT', label: 'Tournament' },
  { value: 'FRIENDLY', label: 'Friendly' },
  { value: 'INTERNATIONAL', label: 'International' },
];

const LeagueForm: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    long_name: '',
    abbreviation: '',
    timeline: '',
    format: '',
    start_date: '',
    end_date: '',
    is_visible: false,
    is_trophy: false,
  });

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      axios.get(API_URL + id + '/').then(res => {
        setForm({
          name: res.data.name || '',
          long_name: res.data.long_name || '',
          abbreviation: res.data.abbreviation || '',
          timeline: res.data.timeline || '',
          format: res.data.format || '',
          start_date: res.data.start_date ? res.data.start_date.slice(0, 10) : '',
          end_date: res.data.end_date ? res.data.end_date.slice(0, 10) : '',
          is_visible: !!res.data.is_visible,
          is_trophy: !!res.data.is_trophy,
        });
      }).catch(() => setError('Failed to load league')).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (isEdit) {
        await axios.put(API_URL + id + '/', form);
        setSuccess('League updated successfully');
      } else {
        await axios.post(API_URL, form);
        setSuccess('League created successfully');
        setForm({
          name: '', long_name: '', abbreviation: '', timeline: '', format: '', start_date: '', end_date: '', is_visible: false, is_trophy: false
        });
      }
      setTimeout(() => navigate('/football/leagues'), 1000);
    } catch (err: any) {
      setError('Failed to save league');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Typography variant="h5" mb={2}>{isEdit ? 'Edit' : 'Create'} Football League</Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
          <TextField label="Long Name" name="long_name" value={form.long_name} onChange={handleChange} fullWidth />
          <TextField label="Abbreviation" name="abbreviation" value={form.abbreviation} onChange={handleChange} fullWidth />
          <TextField select label="Timeline" name="timeline" value={form.timeline} onChange={handleChange} fullWidth>
            {timelineOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
          </TextField>
          <TextField select label="Format" name="format" value={form.format} onChange={handleChange} fullWidth>
            {formatOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
          </TextField>
          <TextField label="Start Date" name="start_date" type="date" value={form.start_date} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label="End Date" name="end_date" type="date" value={form.end_date} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth />
          <FormControlLabel control={<Checkbox checked={form.is_visible} onChange={handleChange} name="is_visible" />} label="Visible" />
          <FormControlLabel control={<Checkbox checked={form.is_trophy} onChange={handleChange} name="is_trophy" />} label="Trophy" />
          {error && <Typography color="error">{error}</Typography>}
          {success && <Typography color="success.main">{success}</Typography>}
          <Button type="submit" variant="contained" disabled={loading}>{isEdit ? 'Update' : 'Create'}</Button>
        </Stack>
      </form>
    </Box>
  );
};

export default LeagueForm; 